import React, { useState } from "react";
import RadioOption from "../Inputs/RadioOption";
import SubtleButton from "../Buttons/SubtleButton";
import AuthLabeledTextInput from "../Inputs/AuthLabeledTextInput";
import axios from "../../services/axios/backendAxios";
import flash from "../../services/flash";
import CheckBox from "../Inputs/CheckBox";
import RateRange from "../Inputs/RateRange";
import { useChangePlanMutation } from "../Billing/Subscriptions";
import { useParkModePlan, PARK_MODE_PLAN_ID } from "../Billing/parkMode";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import errors from "../../services/errors";
import { useApplyCouponCodeMutation } from "../../queries/mutations";
const UnsubscribeForm = ({ subscriptionId, currentUser }) => {
  const [form, setForm] = useState({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const cancelMutation = useMutation({
    mutationFn: async (additionalData) => {
      const survey = {
        ...form,
        ...additionalData,
      };
      console.log("survey", survey);
      await axios.post("stripe/subscription/unsubscribe", {
        subscriptionId,
        survey: survey,
      });
    },
    onSuccess: () => {
      flash.success(
        "Successfully unsubscribed. Your subscription will end at the end of this billing period.",
      );
      queryClient.invalidateQueries({
        queryKey: [`user/${currentUser.uid}/account`],
      });
      navigate("/admin/settings/billing");
    },
    onError: (err) => {
      errors.report(err);
      flash.error("Error: " + (err.message ?? err));
    },
  });
  if (form.reason == null || form.reason == "") {
    return <Reason setForm={setForm} />;
  } else {
    switch (form.reason) {
      case "not-needed":
        if (form.missingFeature) {
          return <FinalThoughts cancelMutation={cancelMutation} />;
        }
        return <NotNeeded setForm={setForm} />;
      case "switching":
        if (form.competitorName) {
          return <FinalThoughts cancelMutation={cancelMutation} />;
        }
        return <SwitchToCompetitor setForm={setForm} form={form} />;
      case "too-expensive":
        return (
          <TooExpensive
            cancelMutation={cancelMutation}
            subscriptionId={subscriptionId}
          />
        );
      case "missing-feature":
        if (form.missingFeature) {
          return <FinalThoughts cancelMutation={cancelMutation} />;
        }
        return <MissingFeature setForm={setForm} />;
      case "usability":
        if (form.suggestions) {
          return <FinalThoughts cancelMutation={cancelMutation} />;
        }
        return <Usability setForm={setForm} />;
      case "customer-support":
        if (form.suggestions) {
          return <FinalThoughts cancelMutation={cancelMutation} />;
        }
        return <CustomerSupport setForm={setForm} />;
      case "out-of-season":
        return (
          <OutOfSeasonConnected
            setForm={setForm}
            cancelMutation={cancelMutation}
          />
        );
      case "other":
        if (form.otherReason) {
          return <FinalThoughts cancelMutation={cancelMutation} />;
        }
        return <OtherReason setForm={setForm} />;
    }
  }
};
function Reason({ setForm }) {
  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setForm((current) => {
            return {
              ...current,
              reason: e.target.reason.value,
            };
          });
        }}
      >
        <h3>What is your primary reason for leaving?</h3>
        <RadioOption required={true} name="reason" value="not-needed">
          I no longer need an employee management solution
        </RadioOption>
        <RadioOption name="reason" value="switching">
          Switching to a competitor
        </RadioOption>
        <RadioOption name="reason" value="too-expensive">
          Too Expensive
        </RadioOption>
        <RadioOption name="reason" value="missing-feature">
          Missing Key Features
        </RadioOption>
        <RadioOption name="reason" value="usability">
          Usability Issues (difficult to use or navigate)
        </RadioOption>
        <RadioOption name="reason" value="customer-support">
          Customer Support Concerns
        </RadioOption>
        <RadioOption name="reason" value="out-of-season">
          Out of Season
        </RadioOption>
        <RadioOption name="reason" value="other">
          Other Reason
        </RadioOption>
        <NextButton />
      </form>
    </>
  );
}
function NextButton({ children, buttonRole = "primary" }) {
  return (
    <SubtleButton
      className="flex items-center gap-1"
      type="submit"
      buttonRole={buttonRole}
      testId="next-button"
    >
      {children ?? "Next"}
    </SubtleButton>
  );
}
function SwitchToCompetitor({ setForm }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setForm((current) => {
          return {
            ...current,
            competitorName: e.target.name.value,
          };
        });
      }}
    >
      <h3>Which competitor are you switching to?</h3>
      <AuthLabeledTextInput
        label="Competitor Name"
        name="name"
        required={true}
      />
      <NextButton />
    </form>
  );
}
function NotNeeded({ setForm }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setForm((current) => {
          return {
            ...current,
            missingFeature: e.target["missing-feature"]?.value,
          };
        });
      }}
    >
      <AuthLabeledTextInput
        label="What feature or improvement would have kept you as a customer?"
        name="missing-feature"
        required={true}
      />
      <NextButton />
    </form>
  );
}
function TooExpensive({ cancelMutation, subscriptionId }) {
  const [cancelAnyway, setCancelAnyway] = useState(false);
  const navigate = useNavigate();
  const applyCouponMutation = useApplyCouponCodeMutation();
  if (cancelAnyway) {
    return <FinalThoughts cancelMutation={cancelMutation} />;
  }
  return (
    <>
      <p>
        We would like to offer you 50% off your subscription for the next 6
        months.
      </p>
      <div className="flex gap-2 mt-2">
        <SubtleButton
          onClick={() =>
            applyCouponMutation
              .mutateAsync({ promotionCode: "QNXOWLXJ", subscriptionId })
              .then(() => navigate("/admin/"))
          }
          buttonRole="primary"
          loading={applyCouponMutation.isPending}
          testId="accept-offer-button"
        >
          Accept The Offer
        </SubtleButton>
        <SubtleButton onClick={() => setCancelAnyway(true)}>
          No Thanks
        </SubtleButton>
      </div>
    </>
  );
}
function OutOfSeason({ cancelMutation, currentUser }) {
  const changePlanMutation = useChangePlanMutation(currentUser);
  const { plan: parkPlan, description: parkDescription } = useParkModePlan();
  const [cancelAnyway, setCancelAnyway] = useState(false);
  const navigate = useNavigate();
  if (cancelAnyway) {
    return <FinalThoughts cancelMutation={cancelMutation} />;
  }
  return (
    <>
      <p>{parkDescription}</p>
      <div className="flex gap-2 mt-2">
        <SubtleButton
          onClick={async () => {
            const changed = await changePlanMutation
              .mutateAsync({ plan: { id: PARK_MODE_PLAN_ID } })
              .catch(() => false);
            if (changed) navigate("/admin/settings/billing");
          }}
          buttonRole="primary"
          loading={changePlanMutation.isPending}
          disabled={!parkPlan}
          testId="switch-to-park-button"
        >
          Switch to Park mode
        </SubtleButton>
        <SubtleButton
          onClick={() => setCancelAnyway(true)}
          testId="no-thanks-button"
        >
          No Thanks
        </SubtleButton>
      </div>
    </>
  );
}
const OutOfSeasonConnected = connect((state) => {
  return {
    currentUser: state.currentUser,
  };
})(OutOfSeason);
function MissingFeature({ setForm }) {
  const featureList = [
    "Employee Scheduling",
    "Time Tracking",
    "Payroll Integration",
    "Job Costing And Invoicing",
    "GPS Tracking And Route Optimization",
    "Client Communication Tools",
  ];
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        console.log(e.target);
        const usefulFeatures = featureList.filter((f) => e.target[f]?.checked);
        // console.log('usefulFeatures', usefulFeatures);
        setForm((current) => {
          return {
            ...current,
            missingFeature: e.target["missing-feature"]?.value,
            usefulFeatures,
          };
        });
      }}
    >
      <p>What features did you find the most valuable within SnowScape?</p>
      <ul>
        {featureList.map((f) => (
          <li key={f}>
            <CheckBox name={f} label={f} />
          </li>
        ))}
      </ul>
      <AuthLabeledTextInput
        label="*What features would have made your experience stronger within SnowScape?"
        name="missing-feature"
        required={true}
      />
      <NextButton />
    </form>
  );
}
function Usability({ setForm }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setForm((current) => {
          return {
            ...current,
            suggestions: e.target.suggestions.value,
            experienceRating: e.target["experience-rating"]?.value,
          };
        });
      }}
    >
      <RateRange name="experience-rating">
        How would you rate your overall experience using SnowScape?
      </RateRange>
      <AuthLabeledTextInput
        label="*Explain your answer about your overall experience"
        name="suggestions"
        required={true}
      />
      <NextButton />
    </form>
  );
}
function CustomerSupport({ setForm }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setForm((current) => {
          return {
            ...current,
            suggestions: e.target.suggestions.value,
            customerSupportRating: e.target["support-rating"]?.value,
          };
        });
      }}
    >
      <RateRange name="support-rating">
        How would you rate our customer support?
      </RateRange>
      <AuthLabeledTextInput
        label="*Explain your answer about our customer support"
        name="suggestions"
        required={true}
      />
      <NextButton />
    </form>
  );
}
function OtherReason({ setForm }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setForm((current) => {
          return {
            ...current,
            otherReason: e.target.reason.value,
          };
        });
      }}
    >
      <AuthLabeledTextInput
        label="*Reason for leaving"
        name="reason"
        required={true}
      />
      <NextButton />
    </form>
  );
}

function FinalThoughts({ cancelMutation }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        cancelMutation.mutateAsync({
          useAgain: e.target["use-again"].value,
          email:
            e.target.email?.value?.length > 0
              ? e.target.email?.value
              : undefined,
          finalSuggestions: e.target.suggestions.value,
        });
      }}
    >
      <h3>*Would you consider using our services again in the future?</h3>
      <RadioOption name="use-again" value="yes" required={true}>
        Yes
      </RadioOption>
      <RadioOption name="use-again" value="no">
        No
      </RadioOption>
      <RadioOption name="use-again" value="maybe">
        Maybe
      </RadioOption>

      <h3>For future updates, leave your email below</h3>
      <AuthLabeledTextInput label="Email Address" name="email" />

      <AuthLabeledTextInput
        name="suggestions"
        label="*Any final thoughts or suggestions for improvement?"
        required={true}
      />
      <SubtleButton
        className="flex items-center gap-1"
        type="submit"
        buttonRole="primary"
        loading={cancelMutation.isPending}
        testId="cancel-sub-button"
      >
        Cancel Subscription
      </SubtleButton>
    </form>
  );
}

export default connect((state) => {
  return {
    currentUser: state.currentUser,
  };
})(UnsubscribeForm);
